import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBagShopping,
  faBed,
  faBus,
  faCar,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faCircleInfo,
  faHeart,
  faLocationDot,
  faMinusCircle,
  faPlane,
  faShip,
  faStar,
  faThumbsUp,
  faTrain,
  faUtensils,
} from '@fortawesome/free-solid-svg-icons';
import { Flag, MaskIcon } from './SharedBits';

/**
 * The shared view's other tabs, ported from the app's view-itinerary.tsx (shared mode): transport
 * tickets, stay cards, points of interest + booked activities, and the budget donut
 * (ExpenseTotalBudget). Dates in a shared trip sit on the 1900 anchor and read as "Day N".
 */

const DAY = 24 * 60 * 60 * 1000;
const PRIMARY = '#074E75';

function toMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string') {
    const ms = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T12:00:00Z` : v);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof v === 'number') return v;
  if (typeof v === 'object' && (v.seconds ?? v._seconds) != null) return (v.seconds ?? v._seconds) * 1000;
  return null;
}

const utcDay = (ms) => Math.floor(ms / DAY);

/** "Day N" relative to the trip start (formatTripDate for a flexible trip). */
function dayLabel(v, trip) {
  const ms = toMs(v);
  const start = toMs(trip?.startDate);
  if (ms == null || start == null) return null;
  return `Day ${utcDay(ms) - utcDay(start) + 1}`;
}

/** Groups items by a "YYYY-MM-DD" key, sorted, the way the app's ticket timelines do. */
function groupByDay(items, keyOf, sortWithin) {
  const map = new Map();
  for (const it of items) {
    const k = keyOf(it) || 'no-date';
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(it);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([k, list]) => [k, [...list].sort(sortWithin)]);
}

const TicketTimeline = ({ groups, trip, render }) => (
  <div className="si-tickets">
    {groups.map(([key, list], gi) => (
      <div key={key} className="si-ticket-group">
        <div className="si-ticket-date-row">
          <div className="si-ticket-marker">
            <span className="si-ticket-dot" />
            {gi < groups.length - 1 && <span className="si-ticket-line" />}
          </div>
          <span className="si-ticket-date">{key === 'no-date' ? 'No Date Set' : dayLabel(key, trip)}</span>
        </div>
        <div className="si-ticket-row">{list.map(render)}</div>
      </div>
    ))}
  </div>
);

const Times = ({ items }) => (
  <div className="si-ticket-times">
    {items.map(([label, value], i) => (
      <div key={i} className="si-ticket-time">
        <span className="si-ticket-time-label">{label}</span>
        <span className="si-ticket-time-value">{value}</span>
      </div>
    ))}
  </div>
);

const Rating = ({ value }) => (typeof value === 'number' ? (
  <span className="si-rating">
    <span className="si-rating-dot" />
    {value.toFixed(1)}
    <FontAwesomeIcon icon={faStar} className="si-rating-star" />
  </span>
) : null);

// ── Transport ────────────────────────────────────────────────────────────────

const COMMUTE_ICON = { flight: faPlane, train: faTrain, boat: faShip, ferry: faShip, bus: faBus, car: faCar };
const COMMUTE_ORDER = { flight: 1, train: 2, boat: 3, bus: 4, car: 5 };

const cityOf = (name) => {
  if (!name) return 'Unknown';
  const parts = name.split(',').map((s) => s.trim());
  return parts.length >= 2 ? `${parts[0]}, ${parts[1]}` : parts[0];
};
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

function utcMinutes(part) {
  if (!part?.date || !part?.time) return null;
  const m = /^([+-]?)(\d+)$/.exec(String(part.utc ?? '+0').trim());
  const off = m ? (m[1] === '-' ? -1 : 1) * Number(m[2]) : 0;
  const ms = Date.parse(`${part.date}T${hhmm(part.time)}:00Z`);
  return Number.isNaN(ms) ? null : ms / 60000 - off * 60;
}

function durationText(c) {
  const a = utcMinutes(c.departureDate);
  const b = utcMinutes(c.arrivalDate);
  if (a == null || b == null) return '-';
  const mins = Math.round(b - a);
  if (mins < 1) return '<1m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

const RouteSide = ({ label, loc, code }) => (
  <div className="si-route-side">
    <span className="si-route-label">{label}</span>
    <span className="si-route-city">
      <span className="si-route-city-name">{cityOf(loc?.name)}</span>
      {loc?.country?.code ? <Flag code={loc.country.code} className="si-route-flag" /> : null}
    </span>
    <span className="si-route-code">{code || ''}</span>
  </div>
);

export const TransportTab = ({ commutes, trip }) => {
  const groups = useMemo(() => groupByDay(commutes, (c) => c.departureDate?.date, (a, b) => {
    const ta = a.departureDate?.time || '';
    const tb = b.departureDate?.time || '';
    if (ta && tb) return ta.localeCompare(tb);
    if (ta || tb) return ta ? -1 : 1;
    return (COMMUTE_ORDER[a.commuteType] || 6) - (COMMUTE_ORDER[b.commuteType] || 6);
  }), [commutes]);
  return (
    <TicketTimeline
      groups={groups}
      trip={trip}
      render={(c, i) => {
        const dep = c.departureDate || {};
        const arr = c.arrivalDate || {};
        const plusDays = dep.date && arr.date ? utcDay(toMs(arr.date)) - utcDay(toMs(dep.date)) : 0;
        return (
          <div key={c.id || i} className="si-ticket">
            <div className="si-ticket-head">
              <FontAwesomeIcon icon={COMMUTE_ICON[c.commuteType] || faCar} className="si-ticket-type" />
              <span className="si-ticket-head-date">{dayLabel(dep.date, trip) || 'TBD'}</span>
            </div>
            <div className="si-ticket-body">
              <div className="si-route">
                <RouteSide label="From" loc={c.departureLocation} code={c.departureAirportCode} />
                <FontAwesomeIcon icon={faArrowRight} className="si-route-arrow" />
                <RouteSide label="To" loc={c.arrivalLocation} code={c.arrivalAirportCode} />
              </div>
              <Times
                items={[
                  ['Departure', dep.time ? `${hhmm(dep.time)} (UTC${dep.utc ?? '+0'})` : '-'],
                  [<>Arrival{plusDays > 0 ? <span className="si-plus-day"> +{plusDays}</span> : null}</>,
                    arr.time ? `${hhmm(arr.time)} (UTC${arr.utc ?? '+0'})` : '-'],
                ]}
              />
              <div className="si-ticket-duration">
                <span className="si-ticket-time-label">Duration</span>
                <span className="si-ticket-time-value">{durationText(c)}</span>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

// ── Stays ────────────────────────────────────────────────────────────────────

const STAY_ORDER = { hotel: 1, hostel: 2, apartment: 3, house: 4, cabin: 5, camping: 6 };

const InfoCard = ({ photo, rows }) => (
  <div className="si-info-row">
    {photo ? <img className="si-info-photo" src={photo} alt="" /> : null}
    <div className="si-info-cols">
      {rows.map(([label, value]) => (
        <div key={label} className="si-info-col">
          <span className="si-info-label">{label}</span>
          <span className="si-info-value">{value}</span>
        </div>
      ))}
    </div>
  </div>
);

export const StaysTab = ({ stays, trip }) => {
  const groups = useMemo(() => groupByDay(stays, (a) => (typeof a.checkInDate === 'string' ? a.checkInDate.split('T')[0] : null), (a, b) => {
    const ta = a.checkInTime || '';
    const tb = b.checkInTime || '';
    if (ta && tb && ta !== tb) return ta.localeCompare(tb);
    return (STAY_ORDER[a.type] || 7) - (STAY_ORDER[b.type] || 7);
  }), [stays]);
  return (
    <TicketTimeline
      groups={groups}
      trip={trip}
      render={(a, i) => {
        const inMs = toMs(a.checkInDate);
        const outMs = toMs(a.checkOutDate);
        const n = inMs != null && outMs != null ? Math.ceil((outMs - inMs) / DAY) : null;
        const loc = a.location || {};
        return (
          <div key={a.id || i} className="si-ticket">
            <div className="si-ticket-head">
              <span className="si-ticket-head-left">
                <FontAwesomeIcon icon={faBed} className="si-ticket-type" />
                <Rating value={loc.rating} />
              </span>
              <span className="si-ticket-head-date">{n == null ? '' : n <= 1 ? '1 night' : `${n} nights`}</span>
            </div>
            <div className="si-ticket-body">
              <InfoCard
                photo={loc.photoUrl}
                rows={a.type === 'address'
                  ? [['Address', loc.address || 'Unknown Address']]
                  : [['Name', loc.name || 'Unknown Name'], ['Location', loc.locality || 'Unknown Location']]}
              />
              <Times items={[['Check-in', dayLabel(a.checkInDate, trip) || '-'], ['Check-out', dayLabel(a.checkOutDate, trip) || '-']]} />
            </div>
          </div>
        );
      }}
    />
  );
};

// ── Points of interest + booked activities ──────────────────────────────────

const RANKS = {
  loved: { label: 'Loved it', icon: faHeart, color: '#E2725B' },
  liked: { label: 'Liked it', icon: faThumbsUp, color: PRIMARY },
  meh: { label: 'Meh', icon: faMinusCircle, color: '#888' },
};
const TOUR_ORDER = ['city', 'culture', 'food', 'nature', 'adventure', 'water', 'wellness', 'spiritual', 'other'];
const PLACE_URL = (id) => `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(id)}`;

const PlacesSection = ({ places }) => {
  const sorted = [...places].sort((a, b) => (a.city || '').localeCompare(b.city || ''));
  const multiCity = new Set(places.map((p) => p.city || '')).size > 1;
  return (
    <div>
      <h3 className="si-section-title">Points of interest</h3>
      <p className="si-section-label">Been there · {sorted.length}</p>
      {sorted.map((p, i) => {
        const cityBreak = multiCity && (i === 0 || (sorted[i - 1].city || '') !== (p.city || ''));
        const rank = RANKS[p.rank];
        return (
          <React.Fragment key={p.googlePlaceId}>
            {cityBreak && <p className="si-city-label">{p.city || 'Elsewhere'}</p>}
            <a className="si-place-row" href={PLACE_URL(p.googlePlaceId)} target="_blank" rel="noopener noreferrer">
              {p.thumbnailUrl
                ? <img className="si-place-thumb" src={p.thumbnailUrl} alt="" />
                : <span className="si-place-thumb si-place-thumb-empty"><FontAwesomeIcon icon={faLocationDot} /></span>}
              <span className="si-place-text">
                <span className="si-place-name">{p.name}</span>
                {!multiCity && p.city ? <span className="si-place-meta">{p.city}</span> : null}
              </span>
              {rank && (
                <span className="si-rank-pill" style={{ color: rank.color, backgroundColor: `${rank.color}18` }}>
                  <FontAwesomeIcon icon={rank.icon} />
                  {rank.label}
                </span>
              )}
            </a>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const PlacesTab = ({ places, tours, trip }) => {
  const groups = useMemo(() => groupByDay(tours, (t) => (typeof t.startDate === 'string' ? t.startDate.split('T')[0] : null), (a, b) => {
    const sa = toMs(a.startDate);
    const sb = toMs(b.startDate);
    if (sa != null && sb != null && sa !== sb) return sa - sb;
    if ((sa == null) !== (sb == null)) return sa == null ? 1 : -1;
    const ia = TOUR_ORDER.indexOf(a.category);
    const ib = TOUR_ORDER.indexOf(b.category);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  }), [tours]);
  return (
    <div className="si-places-tab">
      {places.length > 0 && <PlacesSection places={places} />}
      {tours.length > 0 && (
        <>
          <h3 className="si-section-title" style={{ marginTop: places.length > 0 ? 28 : 0 }}>Booked activities</h3>
          <TicketTimeline
            groups={groups}
            trip={trip}
            render={(t, i) => {
              const s = toMs(t.startDate);
              const e = toMs(t.endDate);
              const n = s != null && e != null ? Math.ceil((e - s) / DAY) + 1 : null;
              const loc = t.location || {};
              return (
                <div key={t.id || i} className="si-ticket">
                  <div className="si-ticket-head">
                    <span className="si-ticket-head-left">
                      <span className="si-ticket-type"><MaskIcon name="tour-guide" size={16} /></span>
                      {t.category ? (
                        <>
                          <span className="si-category-dot" />
                          <span className="si-category-badge">{t.category.charAt(0).toUpperCase() + t.category.slice(1)}</span>
                        </>
                      ) : null}
                      <Rating value={loc.rating} />
                    </span>
                    <span className="si-ticket-head-date">{n == null ? '' : n > 1 ? `${n} days` : '1 day'}</span>
                  </div>
                  <div className="si-ticket-body">
                    <InfoCard photo={loc.photoUrl} rows={[['Name', t.tourName || loc.name || 'Unnamed Tour'], ['Location', loc.locality || '-']]} />
                    <Times items={[['Start', dayLabel(t.startDate, trip) || '-'], ['End', dayLabel(t.endDate, trip) || '-']]} />
                  </div>
                </div>
              );
            }}
          />
        </>
      )}
    </div>
  );
};

// ── Budget (ExpenseTotalBudget, projected) ──────────────────────────────────

const CATEGORIES = [
  { id: 'flights', name: 'Flights', icon: faPlane },
  { id: 'transportation', name: 'Transportation', icon: faCar },
  { id: 'accommodation', name: 'Accommodation', icon: faBed },
  { id: 'food', name: 'Food & Dining', icon: faUtensils },
  { id: 'activities', name: 'Activities & Tours', mask: 'tour-guide' },
  { id: 'shopping', name: 'Shopping', icon: faBagShopping },
  { id: 'other', name: 'Other', icon: faCircle },
];
const SYMBOLS = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', INR: '₹', BRL: 'R$' };

const CatIcon = ({ cat, color, size = 16 }) => (
  <span className="si-cat-icon" style={{ color, width: size, height: size, fontSize: size }}>
    {cat.mask ? <MaskIcon name={cat.mask} size={size} /> : <FontAwesomeIcon icon={cat.icon} />}
  </span>
);

/** calculateProjectedExpenses: flights stay one-off, other categories become a daily rate × trip days. */
function project(expenses, tripDays) {
  const out = expenses.filter((e) => e.category === 'flights');
  const byCat = new Map();
  for (const e of expenses) {
    if (e.category === 'flights') continue;
    const k = e.category || 'other';
    if (!byCat.has(k)) byCat.set(k, []);
    byCat.get(k).push(e);
  }
  for (const [category, list] of byCat) {
    const sum = list.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const sumPax = list.reduce((s, e) => s + (Number(e.amountPax) || 0), 0);
    const dates = new Set(list.map((e) => (e.expenseDate ? String(e.expenseDate).slice(0, 10) : null)).filter(Boolean));
    const d = dates.size || Math.max(1, Math.ceil(list.length / 2));
    out.push({
      category,
      currency: list[0].currency,
      numberOfPeople: list[0].numberOfPeople,
      amount: (sum / d) * tripDays,
      amountPax: (sumPax / d) * tripDays,
    });
  }
  return out;
}

function arcPath(cx, cy, r, a0, a1) {
  const x0 = cx + r * Math.cos(a0);
  const y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1);
  const y1 = cy + r * Math.sin(a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1} ${y1}`;
}

export const BudgetTab = ({ budget, trip }) => {
  const [perPerson, setPerPerson] = useState(true);
  const [view, setView] = useState(0);
  const start = toMs(trip?.startDate);
  const end = toMs(trip?.endDate);
  const tripDays = start != null && end != null ? Math.max(1, Math.ceil((end - start) / DAY)) : null;
  const expenses = tripDays ? project(budget, tripDays) : budget;
  const value = (e) => (perPerson ? Number(e.amountPax) : Number(e.amount)) || 0;
  const total = expenses.reduce((s, e) => s + value(e), 0);
  const byCat = {};
  for (const e of expenses) byCat[e.category || 'other'] = (byCat[e.category || 'other'] || 0) + value(e);
  const chart = CATEGORIES.map((c) => ({ ...c, amount: byCat[c.id] || 0, pct: total ? ((byCat[c.id] || 0) / total) * 100 : 0 }))
    .sort((a, b) => b.amount - a.amount);
  const code = expenses[0]?.currency || 'EUR';
  const symbol = SYMBOLS[code] || code;
  const fmt = (n) => Math.round(n).toLocaleString();

  const slices = chart.filter((c) => c.amount > 0);
  const R = 70;
  const C = 90;
  let angle = -Math.PI + ((total % 1000) / 1000) * 2 * Math.PI;

  return (
    <div className="si-budget-wrap">
      <h3 className="si-budget-title">Trip Budget</h3>
      <div className="si-budget">
        {total > 0 && (
          <div className="si-budget-head">
            <div className="si-mode-toggle">
              <button type="button" className={perPerson ? 'si-mode-active' : ''} onClick={() => setPerPerson(true)}>Per Person</button>
              <button type="button" className={!perPerson ? 'si-mode-active' : ''} onClick={() => setPerPerson(false)}>Total</button>
            </div>
            <div className="si-view-nav">
              <button type="button" disabled={view === 0} onClick={() => setView(0)} aria-label="Chart"><FontAwesomeIcon icon={faChevronLeft} /></button>
              <button type="button" disabled={view === 1} onClick={() => setView(1)} aria-label="Breakdown"><FontAwesomeIcon icon={faChevronRight} /></button>
            </div>
          </div>
        )}
        {total > 0 && tripDays && (
          <div className="si-budget-note"><FontAwesomeIcon icon={faCircleInfo} /> Estimated budget</div>
        )}
        <div className="si-budget-content">
          {total <= 0 ? (
            <p className="si-budget-empty">No amounts set yet</p>
          ) : view === 0 ? (
            <div className="si-donut">
              <svg width="180" height="180" viewBox="0 0 180 180" overflow="visible">
                {slices.length === 1 ? (
                  <circle cx={C} cy={C} r={R} stroke={PRIMARY} strokeWidth="30" fill="none" />
                ) : slices.map((s) => {
                  const a0 = angle;
                  const a1 = angle + (s.pct / 100) * 2 * Math.PI;
                  angle = a1;
                  return (
                    <g key={s.id}>
                      <path d={arcPath(C, C, R, a0, a1)} stroke="#fff" strokeWidth="32" fill="none" />
                      <path d={arcPath(C, C, R, a0 + 0.012, a1 - 0.012)} stroke={PRIMARY} strokeWidth="30" fill="none" />
                    </g>
                  );
                })}
                <circle cx={C} cy={C} r="55" fill="#f8f9fa" />
              </svg>
              {(() => {
                let a = -Math.PI + ((total % 1000) / 1000) * 2 * Math.PI;
                return slices.map((s) => {
                  const mid = a + (s.pct / 100) * Math.PI;
                  a += (s.pct / 100) * 2 * Math.PI;
                  if (s.pct < 7) return null;
                  return (
                    <React.Fragment key={s.id}>
                      <span className="si-donut-icon" style={{ left: C + R * Math.cos(mid), top: C + R * Math.sin(mid) }}>
                        <CatIcon cat={s} color="#fff" />
                      </span>
                      <span className="si-donut-pct" style={{ left: C + 101 * Math.cos(mid), top: C + 101 * Math.sin(mid) }}>
                        {s.pct.toFixed(0)}%
                      </span>
                    </React.Fragment>
                  );
                });
              })()}
              <div className="si-donut-center">
                <span className="si-donut-total">{fmt(total)}</span>
                <span className="si-donut-currency">{symbol} {code}</span>
              </div>
            </div>
          ) : (
            <div className="si-legend">
              {chart.map((c) => (
                <div key={c.id} className="si-legend-item">
                  <CatIcon cat={c} color={c.amount > 0 ? PRIMARY : '#ccc'} />
                  <span className="si-legend-value">
                    {c.amount > 0 ? (
                      <><b>{symbol} {fmt(c.amount)}</b><span className="si-legend-pct"> ({c.pct.toFixed(0)}%)</span></>
                    ) : <span className="si-legend-zero">{symbol} 0</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
